import { useParams } from 'react-router';
import { Check, X } from 'lucide-react';
import { DetailPageStatus } from '@/components/detail/DetailPageStatus';
import { TimeSheetDetailView } from '@/components/timesheet/TimeSheetDetail';
import { loadTimeSheetDetail } from '@/components/timesheet/timeSheetDetailData';
import { toSheetSummary, useValidationDialogs } from '@/components/timesheet/useValidationDialogs';
import { Button } from '@/components/ui/button';
import { useFollowEntityCompany } from '@/hooks/useFollowEntityCompany';
import { useLoader } from '@/hooks/useLoader';
import { reviewActions, reviewerOf } from '@/components/timesheet/reviewActions';
import { useCurrentCompany } from '@/stores/useCompanyStore';

const BACK = { to: '/validation', label: 'Toutes les feuilles à valider' };

/**
 * Feuille d'un employé vue par le Dirigeant ou le manager de son équipe (/validation/{id}) : le détail partagé avec Mes
 * demandes (TimeSheetDetailView), l'employé dans le titre, et Valider / Refuser selon son rôle (reviewActions).
 */
export function ValidationDetailPage() {
  const { id = '' } = useParams();
  const { data, error, loaded, reload } = useLoader(id, loadTimeSheetDetail);
  const { open, dialog } = useValidationDialogs(reload);
  const reviewer = reviewerOf(useCurrentCompany());

  useFollowEntityCompany(data?.period.companyId, '/validation');

  if (!data) {
    return <DetailPageStatus back={BACK} loaded={loaded} error={error} notFound="Feuille introuvable." />;
  }

  const { sheet, period } = data;
  const { validateAs, canReject } = reviewActions(reviewer, sheet);
  const summary = toSheetSummary(sheet, period.monthLabel, validateAs ?? undefined);

  return (
    <>
      <TimeSheetDetailView
        data={data}
        back={BACK}
        title={`${sheet.fullName} — ${period.monthLabel}`}
        actions={
          validateAs && (
            <>
              <Button onClick={() => open('validate', summary)}>
                <Check />
                Valider
              </Button>
              {canReject && (
                <Button
                  variant="outline"
                  className="text-red-600 hover:text-red-700"
                  onClick={() => open('reject', summary)}
                >
                  <X />
                  {sheet.isAbsence ? "Annuler l'absence" : 'Refuser'}
                </Button>
              )}
            </>
          )
        }
      />
      {dialog}
    </>
  );
}
